// Accès privé : page neutre pour un navigateur non autorisé, et ouverture d'un lien d'accès personnel.
import { useEffect, useState } from "react";
import { api, unwrap } from "../api/client";
import { t } from "../lib/i18n";
import { LangSwitch } from "./LangSwitch";
import { Button, FormError } from "./ui";

function Neutral({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center p-4">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-lg border border-line bg-surface p-6 text-center">
        <LangSwitch className="self-end" />
        {children}
      </div>
    </div>
  );
}

/** Ce que voit un navigateur non autorisé : rien sur la gateway, ni son nom, ni l'écran de connexion. */
export function PrivateScreen() {
  const title = t("Service privé");
  useEffect(() => {
    document.title = title;
  }, [title]);
  return (
    <Neutral>
      <h1 className="font-cond text-xl font-semibold tracking-wide">{title}</h1>
      <p className="text-muted">{t("L'accès se fait uniquement sur invitation.")}</p>
    </Neutral>
  );
}

/** Lien d'accès personnel : un clic autorise ce navigateur (un antivirus qui ouvre le lien n'en fait rien). */
export function AccessScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const redeem = async () => {
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.POST("/api/access/redeem", { body: { token } }));
      window.location.hash = "#/";
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Neutral>
      <h1 className="font-cond text-xl font-semibold tracking-wide">{t("Votre lien d'accès")}</h1>
      <p className="text-muted">
        {t("Autorisez ce navigateur pour accéder à la connexion. Faites-le sur votre propre ordinateur : le lien ne sert qu'une fois.")}
      </p>
      <FormError message={error} />
      <Button variant="primary" onClick={redeem} disabled={busy}>{t("Autoriser ce navigateur")}</Button>
    </Neutral>
  );
}
